import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import {
  Alert,
  AlertColor,
  Backdrop,
  CircularProgress,
  Paper,
  Snackbar,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  IconButton,
  ListItemSecondaryAction,
  Stepper,
  Step,
  StepLabel,
  Chip, // Added Chip import
} from "@mui/material";
import { useState, useEffect, ReactNode, ChangeEvent } from "react";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import {
  getServerListService,
  saveServerDetails,
  getS3Policy,
  validateS3Permission
} from "../../services/purecloud";
import { ApiResponse, IServerDetail, IUser } from "../../models";
import { getStorageItem } from "../../services/applicationStorage";

interface S3StorageItem {
  bucketName: string;
  region: string;
}

const ManageCloudStorage = () => {
  const [rows, setRows] = useState<Array<string>>([] as Array<string>);
  const [isFormDataLoading, setFormDataLoading] = useState<boolean>(false);
  const [awsS3Storage, setAwsS3Storage] = useState<Array<S3StorageItem>>([]); // Changed type and initial value
  const [user, setUser] = useState<IUser | null>(null);
  //This hooks is used to show snack bar
  const [getSnackDetails, setSnackDetails] = useState({
    open: false,
    message: (<></>) as ReactNode,
    severity: "success",
  });
  const [getNewServerName, setNewServerName] = useState("");

  // Dialog state for adding S3 storage
  const [openS3Dialog, setOpenS3Dialog] = useState(false);

  const [newS3StorageName, setNewS3StorageName] = useState("");
  const [newS3StorageArn, setNewS3StorageArn] = useState("");

  // Stepper state for S3 dialog
  const [s3Step, setS3Step] = useState(0);
  const [newS3StorageRegion, setNewS3StorageRegion] = useState("");
  const [bucketPolicy, setBucketPolicy] = useState(
    `{
  "Version": "2012-10-17",
  "Statement": [
    {
      "Effect": "Allow",
      "Principal": "*",
      "Action": "s3:GetObject",
      "Resource": "arn:aws:s3:::example-bucket/*"
    }
  ]
}`
  );
  const [validationStatus, setValidationStatus] = useState<null | "success" | "error">(null);
  const [isValidating, setIsValidating] = useState(false);
  const [storageType, setStorageType] = useState<String>("");

  //This hooks is used to load initial data
  useEffect(() => {

    let orgSettings = getStorageItem("org-settings", true, sessionStorage);
    if (orgSettings) {
      orgSettings = JSON.parse(orgSettings);
      setStorageType(orgSettings?.storageType || "");
    }
    
    (async () => {
      try {
        setFormDataLoading(true);
        const apiResponse: ApiResponse<IServerDetail> =
          await getServerListService();
        setRows(apiResponse?.data?.serverList || []);
        // Parse s3List strings into objects
        const parsedS3List = (apiResponse?.data?.s3List || []).map((item: string) => {
          const [bucketName, region] = item.split(":");
          return { bucketName, region };
        });
        setAwsS3Storage(parsedS3List);
        setFormDataLoading(false);
      } catch (err) {
        console.error(err);
        setRows([]);
        setFormDataLoading(false);
        setSnackDetails({
          open: true,
          message: <>Please try again</>,
          severity: "error",
        });
      }
    })();
  }, []);

  useEffect(() => {
    console.log("The S3 Step has changed:", s3Step);
    if (s3Step === 1) {
      setBucketPolicy(`Loading policy...`);
      (async () => {
        try {
          let policyResponse = await getS3Policy(newS3StorageName);
          const policyData = policyResponse?.policy;
          if (policyData) {
            setBucketPolicy(JSON.stringify(policyData, null, 2));
          }
          console.log("S3 Policy Response:", policyResponse);
        }
        catch (err) {
          console.error("Error fetching S3 policy:", err);
        }
      })();
    }
  }, [s3Step]);

  /**
   * This method is called when the form is submitted
   * @param data form data
   */
  const addServer = async () => {
    if (!getNewServerName) {
      return;
    }
    const configuration = {
      orgId: getStorageItem("organization-id", true, sessionStorage),
      serverList: [...rows, getNewServerName],
    };
    try {
      setFormDataLoading(true);
      await saveServerDetails(configuration);
      setSnackDetails({
        open: true,
        message: <>Server Details Updated Successfully</>,
        severity: "success",
      });
      setRows(configuration.serverList);
      setNewServerName("");
      setFormDataLoading(false);
    } catch (err) {
      console.log("setting form :", err);
      setFormDataLoading(false);
      setSnackDetails({
        open: true,
        message: <>Please try again</>,
        severity: "error",
      });
    }
  };

  /**
   * This method is used to delete server individual
   * @param serverName name of the server to be deleted
   * @returns void
   */
  const handleIndividualDeleteClick = (serverName) => async () => {
    const updatedServerList = rows.filter((v) => v !== serverName);
    const configuration = {
      orgId: getStorageItem("organization-id", true, sessionStorage),
      serverList: updatedServerList,
    };
    try {
      setFormDataLoading(true);
      await saveServerDetails(configuration);
      setSnackDetails({
        open: true,
        message: <>Server Details Updated Successfully</>,
        severity: "success",
      });

      setRows(updatedServerList);
      setFormDataLoading(false);
    } catch (err) {
      console.log("handleIndividualDeleteClick failed :", err);
      setFormDataLoading(false);
      setSnackDetails({
        open: true,
        message: <>Please try again</>,
        severity: "error",
      });
    }
  };

  // Delete S3 storage handler
  const handleDeleteS3Storage = (bucketName: string) => {
    setAwsS3Storage((prev) => prev.filter((item) => item.bucketName !== bucketName));

    const configuration = {
      orgId: getStorageItem("organization-id", true, sessionStorage),
      s3List: awsS3Storage
        .filter((item) => item.bucketName !== bucketName)
        .map((item) => `${item.bucketName}:${item.region}`), // Convert back to string for backend
    };
    saveServerDetails(configuration);

    setSnackDetails({
      open: true,
      message: <>S3 Storage "{bucketName}" deleted successfully.</>,
      severity: "success",
    });

  };

  // Add S3 storage handler (final step)
  const handleAddS3Storage = () => {
    const newStorageItem = { bucketName: newS3StorageName.trim(), region: newS3StorageRegion.trim() };
    if (newStorageItem.bucketName && newStorageItem.region && !awsS3Storage.some(item => item.bucketName === newStorageItem.bucketName && item.region === newStorageItem.region)) {
      setAwsS3Storage((prev) => [...prev, newStorageItem]);
      setNewS3StorageName("");
      setNewS3StorageRegion("");
      setValidationStatus(null);
      setOpenS3Dialog(false);
      setS3Step(0);
    }
    const configuration = {
      orgId: getStorageItem("organization-id", true, sessionStorage),
      s3List: [...awsS3Storage.map(item => `${item.bucketName}:${item.region}`), `${newS3StorageName.trim()}:${newS3StorageRegion.trim()}`], // Convert to string for backend
    };
    saveServerDetails(configuration);

  };

  // Reset dialog state on open/close
  const handleOpenS3Dialog = () => {
    setOpenS3Dialog(true);
    setS3Step(0);
    setNewS3StorageName("");
    setNewS3StorageRegion("");
    setValidationStatus(null);
  };

  const handleCloseS3Dialog = () => {
    setOpenS3Dialog(false);
    setS3Step(0);
    setValidationStatus(null);
  };

  // Simulate validation
  const handleValidateConnection = async () => {

    setIsValidating(true);

    try {
      const response = await validateS3Permission(newS3StorageName);
      console.log("Validation Response:", response);
      setValidationStatus("success");
    }
    catch (error) {
      console.error("Validation Error:", error);
      setValidationStatus("error");
    } finally {
      setIsValidating(false);
    }

  };

  const defaultWidthColumns = 50;
  const columns = [
    {
      field: "serverName",
      headerName: "Server",
      minWidth: defaultWidthColumns,
      flex: 1,
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 180,
      cellClassName: "actions",
      getActions: ({ row: { serverName } }) => {
        return [
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            title="Delete"
            onClick={handleIndividualDeleteClick(serverName)}
            color="inherit"
          />,
        ];
      },
    },
  ];

  return (
    <>
      {/* AWS S3 Storage section */}
      <Paper sx={{ width: "100%", overflow: "hidden" }} className="jobs-view">
        <div className="m-2 text">AWS S3 Storage</div>
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={isFormDataLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
        <Box sx={{ margin: "10px 5px 5px 5px", padding: "10px" }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenS3Dialog}
            sx={{ mb: 2, backgroundColor: "#1b2c48", color: "white" }}
          >
            Add S3 Storage
          </Button>
          <List>
            {awsS3Storage.map((storage, idx) => (
              <ListItem key={storage.bucketName}>
                <ListItemText>
                {storage.bucketName}
                <Chip label={storage.region} size="small" sx={{ ml: 2 }} />
                </ListItemText>
                <ListItemSecondaryAction>
                  <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteS3Storage(storage.bucketName)}>
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
            {awsS3Storage.length === 0 && (
              <ListItem>
                <ListItemText primary="No S3 Storage found." />
              </ListItem>
            )}
          </List>
        </Box>
      </Paper>

      {/* Add S3 Storage Dialog with Stepper */}
      <Dialog open={openS3Dialog} onClose={handleCloseS3Dialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add S3 Storage</DialogTitle>
        <DialogContent>

          <Stepper activeStep={s3Step} alternativeLabel sx={{ mb: 2 }}>
            <Step>
              <StepLabel>Bucket Details</StepLabel>
            </Step>
            <Step>
              <StepLabel>Bucket Policy</StepLabel>
            </Step>
            <Step>
              <StepLabel>Validate Connection</StepLabel>
            </Step>
          </Stepper>

          {s3Step === 0 && (
            <Box>
              <TextField
                label="S3 Bucket Name"
                value={newS3StorageName}
                onChange={(e) => setNewS3StorageName(e.target.value)}
                helperText="Enter a unique name for the S3 Storage"
                fullWidth
                margin="normal"
                variant="outlined"
              />
              <TextField
                label="AWS Region"
                value={newS3StorageRegion}
                onChange={(e) => setNewS3StorageRegion(e.target.value)}
                helperText="e.g., us-east-1"
                fullWidth
                margin="normal"
                variant="outlined"
              />
            </Box>
          )}
          {s3Step === 1 && (
            <Box>
              <TextField
                label="Bucket Policy"
                value={bucketPolicy}
                // onChange={(e) => setBucketPolicy(e.target.value)}
                multiline
                minRows={8}
                fullWidth
                margin="normal"
                variant="outlined"
                helperText="Edit the bucket policy as needed"
                InputProps={{ style: { fontFamily: "monospace", fontSize: "12px" } }}
              />
            </Box>
          )}
          {s3Step === 2 && (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mt: 2 }}>
              <Button
                variant="contained"
                sx={{ backgroundColor: "#1b2c48", color: "white", mb: 2 }}
                onClick={handleValidateConnection}
                disabled={isValidating || validationStatus === "success"}
              >
                {isValidating ? "Validating..." : "Validate Connection"}
              </Button>
              {validationStatus === "success" && (
                <Alert severity="success" sx={{ width: "100%" }}>
                  Connection validated successfully!
                </Alert>
              )}
              {validationStatus === "error" && (
                <Alert severity="error" sx={{ width: "100%" }}>
                  Validation failed. Please check your settings.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>

          <Button onClick={handleCloseS3Dialog}>Cancel</Button>

          {s3Step > 0 && (
            <Button onClick={() => setS3Step((s) => s - 1)}>
              Back
            </Button>
          )}

          {s3Step < 2 && (
            <Button
              onClick={() => setS3Step((s) => s + 1)}
              variant="contained"
              sx={{ backgroundColor: "#1b2c48", color: "white" }}
              disabled={
                (s3Step === 0 && (!newS3StorageName.trim() || !newS3StorageRegion.trim() || awsS3Storage.some(item => item.bucketName === newS3StorageName.trim() && item.region === newS3StorageRegion.trim())))
              }
            >
              Next
            </Button>
          )}

          {s3Step === 2 && (
            <Button
              onClick={handleAddS3Storage}
              variant="contained"
              sx={{ backgroundColor: "#1b2c48", color: "white" }}
              disabled={validationStatus !== "success"}
            >
              Add
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ManageCloudStorage;